import { defaultOnOf } from "../settings/catalog";
import { doorNow } from "./cloture";
import { FUNNEL, stepShowKey, type Screen } from "../routes/registry";
import { ORDER_KEY, instancePath, orderedEntries, type Step } from "./order";

/**
 * THE THREAD IS CALCULATED, NEVER WRITTEN DOWN.
 *
 * The prototype's central rule, and the one that took the longest to get right
 * there: two things recompose the funnel — the ORGANISER'S SETTINGS (a step can
 * leave the thread entirely) and, later, the PARTICIPANT'S ANSWER (declining
 * removes the steps that no longer have a subject). The thread then RENUMBERS
 * itself, and Back/Next follow the computed thread rather than the order of the
 * routes.
 *
 * Two steps today, so the arithmetic is trivial — the point is that the structure
 * exists now. Written as a hard-coded pair, every step added later would mean
 * reworking the breadcrumb, the entry button and the navigation at once.
 */

/* Registration is closed: the funnel stops at its first step and says why.

   ⚠️ TROIS FAÇONS DE NE PAS ÊTRE OUVERT, ET LE FIL LES LIT PAR LA MÊME FONCTION —
   `doorNow` : l'interrupteur de l'organisateur, l'échéance qu'il a posée, et l'heure
   d'ouverture pas encore venue (voir `funnel/cloture.ts`). Lire ici la seule clé
   booléenne aurait laissé le fil grand ouvert à l'heure dite, alors que l'écran de
   participation, lui, se serait verrouillé : un parcours qui mène à une porte close est
   exactement le cul-de-sac que ce module calcule pour éviter. */

/** Everyone is registered on arrival: the question is not asked at all. */
const AUTO = "FO_MEETING_V2_REGISTRATION_AUTO";

/**
 * ═══ LE PARCOURS NE POSE PLUS LA QUESTION : QUELQU'UN DOIT Y RÉPONDRE ═══
 *
 * ⚠️ RETIRER LA QUESTION, C'EST S'ENGAGER À POSER LA RÉPONSE. L'écriture finale lit
 * `answers.participation ?? ce que le serveur détient` (voir `saveParticipation`) : sans
 * réponse, elle retombe sur « ne participe pas », `isRegistered: false`, et SUPPRIME tous
 * les jours. L'événement qui voulait inscrire tout le monde n'inscrivait personne, et la
 * confirmation remerciait d'avoir prévenu.
 *
 * ⚠️ ET DEUX RÉGLAGES RETIRENT CETTE ÉTAPE, pas un. `FO_MEETING_V2_REGISTRATION_AUTO`
 * (« tout le monde est inscrit sur place », dit son aide) — celui-là était couvert. Mais
 * l'interrupteur « Servie » de l'étape la retire AUSSI, et là plus personne ne répondait :
 * deux réglages qui donnent le même parcours à l'écran donnaient deux résultats opposés en
 * base. D'où la question posée au FIL CALCULÉ plutôt qu'à un réglage : ce qui compte est
 * que l'écran ne soit plus servi, quelle qu'en soit la raison.
 *
 * ⚠️ « FERMÉ » N'EST PAS « PAS DEMANDÉ ». Un fil clos s'arrête à son premier écran et ne
 * collecte rien : répondre à la place de quelqu'un dont l'inscription ne peut pas avoir
 * lieu serait l'inscrire par accident.
 *
 * ⚠️ ET « PAS ENCORE OUVERT » NON PLUS — c'est le même accident, une semaine plus tôt.
 * L'inscription automatique inscrit à la CONNEXION : sans cette porte, quelqu'un qui
 * ouvre le site la veille de l'ouverture repartait inscrit, et l'organisateur lisait une
 * liste qui grossissait avant l'heure qu'il avait lui-même annoncée.
 *
 * Le châssis s'en sert à l'entrée du tunnel, pour les visiteurs seulement.
 */
export function answersForParticipant(read: Read, offer: Offer = {}): boolean {
  if (doorNow(read) !== "open") return false;
  return !activeSteps(read, offer).some((s) => s.path === PARTICIPATION);
}

type Read = (key: string) => unknown;

function on(read: Read, key: string): boolean {
  const raw = read(key);
  return typeof raw === "boolean" ? raw : defaultOnOf(key);
}

/** L'étape où l'on dit si l'on vient — celle qu'un refus garde. */
const PARTICIPATION = "/registration";

/**
 * L'ÉCRAN QUI REND COMPTE DE L'INSCRIPTION, et la seule adresse écrite en dur du tunnel.
 *
 * ⚠️ ELLE VIT ICI PARCE QUE QUATRE ENDROITS LA VISAIENT, chacun avec sa propre copie de
 * la chaîne : le châssis, le badge, l'enregistrement d'une étape, et ce module. Une
 * cinquième copie serait passée inaperçue le jour où l'adresse change, et le symptôme
 * aurait été un bouton qui mène au 404.
 *
 * Elle n'est pas retirable (voir `removable` dans le registre), donc elle est toujours du
 * fil : on peut y envoyer quelqu'un sans vérifier qu'elle existe.
 */
export const CONFIRMATION = "/registration/confirmation";

/**
 * LES DEUX RÉPONSES QUI VEULENT DIRE « JE NE VIENDRAI PAS ».
 *
 * ⚠️ ET C'EST LA MÊME LISTE QUE CELLE DE L'ÉCRITURE, à l'autre bout du tunnel :
 * `saveParticipation` calcule `coming` sur « sur place ou à distance », donc ces deux-là
 * posent déjà `isRegistered: false` et retirent tous les jours. Le fil et l'écriture
 * doivent dire la même chose — sinon on demande ses conférences à quelqu'un qu'on
 * s'apprête à désinscrire de tout.
 *
 * ⚠️ « REPRÉSENTÉ » EST BIEN DE CE CÔTÉ-LÀ, et ce n'est pas évident au premier regard :
 * quelqu'un VIENDRA, mais pas la personne qui répond. Ses jours, ses conférences, sa
 * société, ses accompagnants n'ont donc aucun sujet — et son remplaçant, lui, est
 * désigné DANS l'étape de participation (le produit y a fondu son ancienne étape
 * « Invitation »), pas dans les écrans qui suivent.
 */
const NOT_COMING = new Set(["decline", "represented"]);
/** The typology step, and what makes it worth showing. */
const TYPOLOGY = "/registration/typology";
/** L'étape des dates, et ce qui lui donne un sujet. */
const DATES = "/registration/dates";

/**
 * CE QUE L'ÉVÉNEMENT PORTE, ET DONT DÉPEND L'EXISTENCE D'UNE ÉTAPE.
 *
 * Toutes les étapes ne sont pas décidées par la seule configuration : certaines n'ont
 * de sujet que si l'événement en a un. Un salon sans typologie n'a pas de profil à
 * demander ; un salon d'un seul jour n'a pas de dates à choisir.
 *
 * ⚠️ UN SAC NOMMÉ PLUTÔT QU'UN PARAMÈTRE DE PLUS, et il a été introduit en en ajoutant
 * le deuxième. Chaque nouvelle liste (les conférences, les articles, les quotas)
 * aurait sinon fait le tour des quinze appels de ce module — dans dix fichiers — et
 * celui qu'on aurait oublié n'aurait pas planté : il aurait calculé un fil DIFFÉRENT
 * des autres. Le fil d'Ariane annonce alors « étape 2 sur 3 » et en sert quatre.
 *
 * ⚠️ CHAQUE CHAMP EST FACULTATIF, ET SON ABSENCE VAUT « JE NE SAIS PAS » — pas
 * « il n'y en a pas ». Un appelant qui ne peut pas savoir ne doit pas faire
 * disparaître une étape en silence.
 */
export type Offer = {
  /** Les typologies de l'événement. */
  typos?: ReadonlyArray<{ id: number | null }>;
  /** Ses jours. */
  days?: ReadonlyArray<unknown>;
  /**
   * ═══ CE QUE LE PARTICIPANT A RÉPONDU — LA SECONDE ENTRÉE QUI RECOMPOSE LE FIL ═══
   *
   * La première est la configuration ; celle-ci est la conversation. « Je ne participe
   * pas » ne retire pas une étape, il retire TOUT CE QUI SUIT : à quoi bon demander son
   * profil, ses jours, ses conférences et ses accompagnants à quelqu'un qui vient de
   * dire qu'il ne vient pas ? Il reste la question, et l'écran qui enregistre sa
   * réponse.
   *
   * ⚠️ ET C'EST BIEN UN FIL RECOMPOSÉ, PAS UN SAUT. Le bouton « Continuer » de l'étape 1
   * mène à la confirmation parce que `stepAfter` lit ce fil-ci — aucun écran n'a de
   * destination écrite en dur, et le fil d'Ariane annonce « étape 1 sur 2 » au lieu de
   * promettre neuf écrans dont sept ne viendront pas.
   *
   * ⚠️ SON ABSENCE VAUT « PAS ENCORE RÉPONDU », comme les autres champs de ce sac : un
   * appelant qui ne peut pas savoir ne réduit rien.
   */
  participation?: string;
  /**
   * ═══ UNE INSCRIPTION EST ENREGISTRÉE — CE QUE LA PORTE CLOSE NE REPREND PAS ═══
   * (tâche #332)
   *
   * C'est `recorded?.responded` : le serveur détient une réponse, pas une réponse en
   * cours dans le tunnel. La clôture refuse les NOUVELLES inscriptions ; elle ne
   * confisque pas à une inscrite l'écran où elle paie ce qu'elle doit et retrouve ses
   * factures — `<Checkout>` et `InvoiceList` ne vivent QUE sur la confirmation. Voir la
   * porte, plus bas.
   *
   * ⚠️ SON ABSENCE VAUT « PAS INSCRIT », et c'est le sens sûr : sans elle, la porte close
   * reste close, comme avant.
   */
  responded?: boolean;
  /**
   * ═══ A PROFILE PICKED IN THIS JOURNEY — THE TYPOLOGY STEP STAYS IN THE THREAD ═══
   *
   * ⚠️ THE ANSWER MUST NOT REMOVE THE QUESTION IT ANSWERS. The participant's typology
   * decides which configuration is served, steps included (`funnel/viewerTypo.ts`). An
   * organiser may switch the typology step off FOR A GIVEN TYPOLOGY — its people are
   * known already, they need not be asked. Somebody whose typology was unknown is
   * served the step under the common configuration; picking that typology recomposed
   * the thread under ITS settings, the step left it on the click, and the shell sent
   * the participant back to the first step ("this step is no longer in the funnel"),
   * their profile lost on the way.
   *
   * It is `answers.typoId !== undefined`: only the typology step writes it. Its
   * absence changes nothing, so a typology known before this journey still skips the
   * step as its configuration says.
   */
  typoChosen?: boolean;
};

export function activeSteps(
  read: Read,
  offer: Offer = {},
): ReadonlyArray<Step> {
  return composeSteps(read, offer, true);
}

/**
 * ═══ LE FIL TEL QUE L'ÉVÉNEMENT LE COMPOSE, PORTE MISE À PART ═══
 *
 * La question que pose le récapitulatif de la confirmation : « cet événement vend-il
 * des billets, a-t-il des conférences, une étape société, pour cette personne ? » —
 * et non « peut-elle encore y passer ». Lue sur le fil actif, la porte close aurait
 * effacé de sa confirmation la commande qu'il lui reste à payer.
 *
 * ⚠️ JAMAIS POUR DÉCIDER OÙ L'ON PEUT ALLER : c'est `activeSteps`, qui écoute la porte.
 */
export function inJourney(
  read: Read,
  path: string,
  offer: Offer = {},
): boolean {
  return composeSteps(read, offer, false).some((s) => s.path === path);
}

function composeSteps(
  read: Read,
  { typos, days, participation, responded, typoChosen }: Offer,
  door: boolean,
): ReadonlyArray<Step> {
  /**
   * ═══ LE FIL PART DE CE QUE L'ORGANISATEUR A RANGÉ, PAS DU REGISTRE ═══
   *
   * L'ordre des étapes — et le nombre d'instances de celles qui se répètent — vit
   * désormais dans la configuration de l'événement. Absent, `orderedEntries` rend
   * exactement l'ordre déclaré : rien ne change pour un événement qui n'y a pas touché.
   * Voir `funnel/order.ts`, qui porte aussi les dépendances qu'un ordre ne peut pas
   * enfreindre.
   */
  const funnel: Step[] = orderedEntries(read(ORDER_KEY)).flatMap((entry) => {
    const declared = FUNNEL.find((s) => s.key === entry.key);
    if (!declared) return [];
    return [
      {
        ...declared,
        ...(entry.instance ? { instance: entry.instance } : {}),
        path: instancePath(declared.path, entry.instance),
      },
    ];
  });

  // A registration that is closed — or not open yet — keeps ONLY the screen that
  // explains it. Letting someone walk further would collect answers into a
  // registration that cannot happen.
  /**
   * ⚠️ SAUF LA CONFIRMATION D'UNE INSCRITE (tâche #332). Réduit à son premier écran, le
   * fil renvoyait l'inscrite sur « Inscriptions closes » : plus de « Payer », plus de
   * factures, alors que « Ma commande » la laissait encore ajouter un billet « à régler
   * à la dernière étape » — le cul-de-sac que `funnel/cloture.ts` interdit. Les étapes
   * qui collectent restent fermées : la confirmation n'écrit que des réponses portées
   * par le tunnel, et il n'y en a plus à porter.
   */
  if (door && doorNow(read) !== "open") {
    const kept = funnel.slice(0, 1);
    const confirmation = funnel.find((s) => s.path === CONFIRMATION);
    return responded === true && confirmation && !kept.includes(confirmation)
      ? [...kept, confirmation]
      : kept;
  }

  // Automatic registration takes the participation step out of the thread — there
  // is nothing to ask.
  const auto = on(read, AUTO);

  /**
   * An event with no selectable profile has nothing to ask here, so the step goes
   * away rather than showing an empty list somebody cannot get past. Either the
   * event has no typology at all, or the organiser has taken every one out of the
   * choice.
   */
  const askTypology =
    !typos ||
    typos.some(
      (t) => t.id !== null && read(`FO_MEETING_V2_TYPO_SHOW_${t.id}`) !== false,
    );

  /**
   * ⚠️ MOINS DE DEUX JOURS, RIEN À CHOISIR — la règle de la V1 (`event.days.length
   * <= 1`), et elle est plus importante qu'elle n'en a l'air : la plupart des
   * événements durent un jour, donc c'est ce test, et non l'interrupteur, qui retire
   * l'étape presque partout. Servie quand même, elle poserait une question à une seule
   * réponse possible.
   *
   * ⚠️ ET RETIRER L'ÉTAPE N'A JAMAIS VOULU DIRE « AUCUN JOUR ». Sans dates à demander,
   * le participant est inscrit à TOUS les jours — c'est ce que fait la V1 en sautant
   * son écran, et c'est écrit à la confirmation. Voir `funnel/dates.ts`.
   */
  const askDates = !days || days.length > 1;

  const steps = funnel.filter((step) => {
    /**
     * THE ORGANISER'S OWN SWITCH, first: one place decides which steps a
     * participant walks (see the "Étapes du parcours" family in the panel). A step
     * that cannot be removed ignores it — the confirmation is where the answers are
     * written.
     *
     * ⚠️ L'ÉTAT EFFECTIF DE L'INTERRUPTEUR, ET NON « retirée sur un `false` ». Les
     * deux disaient la même chose tant que toutes les étapes étaient allumées par
     * défaut ; la société ne l'est pas (voir `optIn` dans le registre), et lue à
     * l'ancienne son absence de clé l'aurait SERVIE — c'est-à-dire l'exact contraire
     * de ce que le panneau affiche, puisque lui lit déjà `onByDefault`.
     */
    /**
     * ⚠️ LE DÉFAUT SE LIT SUR LA CLÉ DE BASE, ET C'EST INDISPENSABLE POUR UNE INSTANCE
     * AJOUTÉE. `defaultOnOf` cherche la clé dans le catalogue STATIQUE, construit sans
     * événement : `…_STEP_INFORMATIONS_SHOW_ab` n'y existe pas, son défaut y vaudrait
     * donc « éteinte », et la deuxième étape « informations » disparaîtrait à peine
     * ajoutée. C'est le même piège que les interrupteurs par typologie, corrigé au même
     * endroit qu'eux — voir `visibleSettings`.
     */
    // Picked here, kept here: see `typoChosen`.
    if (step.path === TYPOLOGY && typoChosen) return true;
    if (step.removable) {
      const raw = read(stepShowKey(step, step.instance));
      const served =
        typeof raw === "boolean" ? raw : defaultOnOf(stepShowKey(step));
      if (!served) return false;
    }
    // Then the rules that follow from other settings.
    /* ⚠️ PAR SA CLÉ, ET NON « la première du fil ». Les deux se confondaient tant que
       l'ordre était figé ; ils se séparent dès que l'organisateur le change, et c'est
       la QUESTION de participation que l'inscription automatique retire, pas la
       première étape venue. */
    if (auto && step.key === "registration") return false;
    if (step.path === TYPOLOGY && !askTypology) return false;
    if (step.path === DATES && !askDates) return false;
    return true;
  });

  /**
   * ═══ « JE NE VIENDRAI PAS » — LA QUESTION, ET SA CONFIRMATION ═══
   *
   * Tout ce qui est au milieu perd son sujet d'un coup : on ne demande ni profil, ni
   * jours, ni conférences, ni accompagnants, ni billetterie à quelqu'un qui vient de
   * dire qu'il ne vient pas. Le prototype le fait déjà — c'est sa règle « le tunnel
   * écoute la réponse » — et c'est aussi ce que le récapitulatif suppose : il n'affiche
   * ni jours ni commande pour un refus.
   *
   * ⚠️ DEUX RÉPONSES, PAS UNE — voir `NE_VIENT_PAS`. Se faire représenter n'est pas venir :
   * la seule chose de plus à recueillir, c'est le nom du remplaçant, et il se donne à
   * l'étape même où l'on répond.
   *
   * ⚠️ LA CONFIRMATION RESTE, ET C'EST TOUT L'INTÉRÊT. C'est le seul écran qui ÉCRIT :
   * un refus doit s'enregistrer (`responded: true`, `isRegistered: false`) aussi
   * sûrement qu'une venue, sinon l'organisateur lit « pas de réponse » de quelqu'un qui
   * a répondu. Elle n'est pas retirable, donc elle survit à ce filtre par construction.
   *
   * ⚠️ ET L'ÉTAPE DE PARTICIPATION RESTE AUSSI : c'est celle où l'on vient de répondre,
   * et celle où l'on revient pour changer d'avis. La retirer aurait enfermé le refus.
   *
   * ⚠️ ELLE SE DÉSIGNE PAR SON CHEMIN, ET NON PAR « LA PREMIÈRE DU FIL » — c'est son test
   * qui l'a imposé. Les deux se confondent tant que l'étape 1 est servie ; elles se
   * séparent dès que l'organisateur la retire, et un refus peut très bien exister dans ce
   * cas (le back office l'a enregistré). Le raccourci gardait alors la TYPOLOGIE, qui
   * n'était devenue première que par accident — soit exactement la question sans sujet
   * que cette règle existe pour supprimer.
   */
  /**
   * ═══ À DISTANCE, PAS D'ACCOMPAGNANTS ═══ (tâche #225)
   *
   * Un accompagnant est une personne de plus SUR PLACE, avec son badge pour la porte
   * (`isRemote: false`, posé à sa création). Qui suit l'événement en ligne n'amène
   * personne : l'étape n'a plus de sujet, comme dans la V1, qui ne propose pas l'ajout à
   * un inscrit à distance (`Confirmation/index.jsx`). Le reste du fil ne bouge pas : on
   * vient, donc on répond au reste.
   */
  const selected = NOT_COMING.has(participation ?? "")
    ? steps.filter((step) => step.path === PARTICIPATION || !step.removable)
    : participation === "remote"
      ? steps.filter((step) => step.key !== "companions")
      : steps;

  // It must not empty the funnel: a thread with no steps has no first step for the
  // home button to reach.
  return selected.length > 0 ? selected : funnel.slice(-1);
}

/**
 * The step reached from `path` — the thread's own order.
 *
 * ⚠️ WITH A FALLBACK ON THE DECLARED ORDER when `path` is not in the thread at all.
 * A participant never sees that case (the shell redirects them out of it), but an
 * ORGANISER does: they configure a step the settings have removed. Returning nothing
 * left their "Continuer" dead, so a step could be switched out of the funnel and
 * then no longer walked through — measured, and it is how automatic registration
 * came to look like a one-way door.
 */
export function stepAfter(
  read: Read,
  path: string,
  offer: Offer = {},
): (Screen & { funnel: string }) | undefined {
  const steps = activeSteps(read, offer);
  const at = steps.findIndex((s) => s.path === path);
  if (at >= 0) return steps[at + 1];

  const declared = FUNNEL.findIndex((s) => s.path === path);
  return declared < 0 ? undefined : FUNNEL[declared + 1];
}

/** Is this step part of what a participant actually walks? */
export function inThread(
  read: Read,
  path: string,
  offer: Offer = {},
): boolean {
  return activeSteps(read, offer).some((s) => s.path === path);
}

/** Where the entry button leads: the first ACTIVE step, not the first declared. */
export function firstStep(
  read: Read,
  offer: Offer = {},
): (Screen & { funnel: string }) | undefined {
  return activeSteps(read, offer)[0];
}

